import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { StarRating } from './StarRating';
import { markSubmitted } from './feedbackStorage';
import { feedbackApi } from '@/utils/api-pb';
import { reachGoal } from '@/utils/analytics';

interface FeedbackModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source?: 'sidebar' | 'proactive';
}

const MAX_LEN = 2000;

export function FeedbackModal({ open, onOpenChange, source = 'sidebar' }: FeedbackModalProps) {
  const { t } = useTranslation('feedback');
  const location = useLocation();

  const [rating, setRating] = useState(0);
  const [questionAnswer, setQuestionAnswer] = useState('');
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const resetState = () => {
    setRating(0);
    setQuestionAnswer('');
    setComment('');
    setIsSubmitting(false);
    setIsSuccess(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      // Reset after the close animation so the form doesn't flash empty
      setTimeout(resetState, 200);
    }
    onOpenChange(next);
  };

  const handleSubmit = async () => {
    if (rating < 1 || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await feedbackApi.submit({
        rating,
        question_answer: questionAnswer.trim() || undefined,
        comment: comment.trim() || undefined,
        source,
        page: location.pathname,
      });

      markSubmitted();
      reachGoal('feedback_submitted');
      setIsSuccess(true);

      // Auto-close after the thank-you screen (enough time to read it)
      setTimeout(() => handleOpenChange(false), 3000);
    } catch (error) {
      const message = error instanceof Error ? error.message : t('error');
      toast.error(message);
      setIsSubmitting(false);
    }
  };

  const starLabels = [1, 2, 3, 4, 5].map((n) => t('rating.starAria', { count: n }));

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-[480px] rounded-[24px] border-none p-0">
        <div className="p-8">
          {isSuccess ? (
            <div className="flex flex-col items-center gap-4 py-6 text-center">
              <CheckCircle2 className="h-16 w-16 text-[#2197F1]" />
              <DialogTitle className="text-[22px] font-bold text-center">
                {t('success.title')}
              </DialogTitle>
              <DialogDescription className="text-[14px] text-[#565858] text-center leading-relaxed">
                {t('success.description')}
              </DialogDescription>
            </div>
          ) : (
            <>
              <DialogHeader className="text-center space-y-2">
                <DialogTitle className="text-[22px] font-bold text-center">
                  {t('title')}
                </DialogTitle>
                <DialogDescription className="text-[14px] text-[#565858] text-center leading-relaxed">
                  {t('subtitle')}
                </DialogDescription>
              </DialogHeader>

              <div className="mt-6 space-y-5">
                {/* Rating */}
                <div className="flex flex-col items-center gap-3">
                  <label className="text-[14px] text-[#565858]">{t('rating.label')}</label>
                  <StarRating
                    value={rating}
                    onChange={setRating}
                    labels={starLabels}
                    disabled={isSubmitting}
                  />
                </div>

                {/* Question */}
                <div className="space-y-2">
                  <label className="text-[14px] text-[#565858]">{t('question.label')}</label>
                  <Textarea
                    value={questionAnswer}
                    onChange={(e) => setQuestionAnswer(e.target.value.slice(0, MAX_LEN))}
                    placeholder={t('question.placeholder')}
                    rows={2}
                    disabled={isSubmitting}
                    className="rounded-xl border-[#eeeeee] text-[16px] min-h-[80px]"
                  />
                </div>

                {/* Comment */}
                <div className="space-y-2">
                  <label className="text-[14px] text-[#565858]">{t('comment.label')}</label>
                  <Textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value.slice(0, MAX_LEN))}
                    placeholder={t('comment.placeholder')}
                    rows={2}
                    disabled={isSubmitting}
                    className="rounded-xl border-[#eeeeee] text-[16px] min-h-[80px]"
                  />
                </div>

                <Button
                  onClick={handleSubmit}
                  disabled={rating < 1 || isSubmitting}
                  className="h-[48px] w-full rounded-[16px] bg-[#2197F1] hover:bg-[#1a7acc] text-white text-[16px] font-semibold"
                >
                  {isSubmitting && <Loader2 className="animate-spin" />}
                  {t('submit')}
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default FeedbackModal;
